import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { pollTask } from "@/lib/api";
import { LoadingState, ErrorState } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { goTop } from "@/lib/embedded";
import { cn } from "@/lib/utils";
import { useBlueprint } from "@/components/assessment/useBlueprint";
import { SetupView, GeneratingView } from "@/components/assessment/SetupView";
import { ReviewView } from "@/components/assessment/ReviewView";

function useElapsed(since) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!since) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [since]);
  return since ? Math.max(0, Math.round((now - since) / 1000)) : 0;
}

// Assessment step: set up the blueprint → generating → review questions with the path to trial, sign-off and publish.
export default function TestStudio({ onChanged }) {
  const { jobId } = useParams();
  const { user } = useAuth();
  const readOnly = user?.org_role === "viewer";
  const nav = useNavigate();
  const bp = useBlueprint(jobId);
  const [state, setState] = useState({ loading: true });
  const [selected, setSelected] = useState(null);
  const [generating, setGenerating] = useState(null);
  const [trialing, setTrialing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const seconds = useElapsed(generating);

  const load = useCallback(async () => {
    const [t, j, g] = await Promise.all([api.get(`/jobs/${jobId}/test`), api.get(`/jobs/${jobId}`), api.get(`/jobs/${jobId}/publish-gate`).catch(() => ({ data: null }))]);
    setState({ loading: false, data: t.data, job: j.data, gate: g.data });
    return t.data;
  }, [jobId]);
  useEffect(() => {
    load().then((d) => setSelected(Number(new URLSearchParams(window.location.search).get("version")) || d?.latest?.version || null))
      .catch(() => setState({ loading: false, error: true }));
  }, [load]);

  const generate = async () => {
    if (readOnly) { toast.error("Your seat is read-only."); return; }
    if (!bp.valid) { toast.error("Check the blueprint: question and difficulty counts must match, and weights must total 100%."); return; }
    if (!(await bp.save())) return;
    setGenerating(Date.now());
    try {
      const { data } = await api.post(`/jobs/${jobId}/test`);
      await pollTask(data.task_id, { interval: 3000, timeout: 330000 });
      const d = await load();
      setSelected(d?.latest?.version || null);
      onChanged?.();
      toast.success(`Draft v${d?.latest?.version} is ready to review`);
    } catch (e) { toast.error(e.message || "Generation did not finish. Your blueprint is saved; please try again."); }
    finally { setGenerating(null); }
  };

  const trial = async (version) => {
    setTrialing(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/test/trial`, version ? { version } : {});
      goTop(nav, `/recruiter/jobs/${jobId}/trial/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start the trial run"); }
    finally { setTrialing(false); }
  };

  const publishVersion = async (v) => {
    if (!window.confirm(`Make v${v} the assessment candidates take? Candidates already in progress keep their current questions.`)) return;
    setPublishing(true);
    try { await api.post(`/jobs/${jobId}/test/${v}/publish`); await load(); onChanged?.(); toast.success(`v${v} is now the live assessment`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not publish this version"); }
    finally { setPublishing(false); }
  };

  if (state.loading) return <LoadingState label="Opening the assessment…" />;
  if (state.error) return <div className="app-container py-8"><ErrorState message="Could not load the assessment." onRetry={() => window.location.reload()} /></div>;
  const { data, job, gate } = state;
  const test = data.versions.find((v) => v.version === selected) || data.latest;
  const view = generating ? "generating" : test ? "review" : "setup";

  return (
    <div className={cn("app-container", view === "review" ? "py-4 md:flex-1 md:min-h-0 md:flex md:flex-col" : "py-6")} data-testid="assessment-builder" data-state={view}>
      {generating ? <GeneratingView seconds={seconds} />
        : !test ? <SetupView jobId={jobId} bp={bp} readOnly={readOnly} onGenerate={generate} />
        : <ReviewView jobId={jobId} job={job} data={data} test={test} setSelected={setSelected} gate={gate} readOnly={readOnly} bp={bp}
            onRegenerate={generate} onTrial={trial} trialing={trialing} onPublish={publishVersion} publishing={publishing} onRefresh={() => { load(); onChanged?.(); }} />}
    </div>
  );
}
